import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CloseButton, useDialogs } from './dialogs';
import { Icon, PlatformIcon } from './icons';
import { Avatar, Badge, DetailRow, Divider, Notice, num, pct } from './ui';
import { timeAgo } from './data';

// The shell's dialogs, after the prototype: ⌘K search, help & shortcuts, notifications, tracking health, the
// workspace and the profile. Each is a body component plus a footer component so both can use hooks.

function useGo() {
  const navigate = useNavigate();
  const { closeDialog } = useDialogs();
  return to => { closeDialog(); navigate(to); };
}

// ── ⌘K ──
export function CommandPalette({ pages }) {
  const [q, setQ] = useState('');
  const go = useGo();
  const qc = useQueryClient();
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, []);
  const query = q.trim().toLowerCase();
  const contacts = useMemo(() => (query ? (qc.getQueryData(['contacts']) || []).filter(c => `${c.name || ''} ${c.email || ''} ${c.phone || ''}`.toLowerCase().includes(query)).slice(0, 6) : []), [qc, query]);
  const campaigns = useMemo(() => {
    if (!query) return [];
    const seen = new Set();
    return qc.getQueriesData({ queryKey: ['analytics', '/report'] }).flatMap(([, data]) => data?.rows || [])
      .filter(r => r.raw !== '(none)' && String(r.name || r.raw).toLowerCase().includes(query) && !seen.has(String(r.raw)) && seen.add(String(r.raw))).slice(0, 6);
  }, [qc, query]);
  const results = [
    ...pages.filter(p => p.label.toLowerCase().includes(query)).map(p => ({ key: `page-${p.path}`, icon: <Icon name={p.icon} />, label: p.label, kind: 'Page', to: p.path })),
    ...campaigns.map(r => ({ key: `campaign-${r.raw}`, icon: <PlatformIcon name="meta" />, label: r.name || r.raw, kind: 'Campaign', to: `/campaigns?q=${encodeURIComponent(r.name || r.raw)}` })),
    ...contacts.map(c => ({ key: `contact-${c.contact_id}`, icon: <Icon name="users" />, label: c.name || c.email || c.phone, kind: 'Customer', to: `/people?contact=${encodeURIComponent(c.contact_id)}` })),
  ];
  const move = e => {
    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
    const buttons = [...e.currentTarget.querySelectorAll('#command-results button')];
    const index = buttons.indexOf(document.activeElement);
    if (e.key === 'Enter') { if (e.target === inputRef.current) { e.preventDefault(); buttons[0]?.click(); } return; }
    e.preventDefault();
    buttons[Math.max(0, Math.min(buttons.length - 1, index + (e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
  };
  const { closeDialog } = useDialogs();
  return (
    <div onKeyDown={move}>
      <div className="command-input"><Icon name="search" />
        <input ref={inputRef} id="command-search" placeholder="Search pages, campaigns, customers..." aria-label="Search workspace" value={q} onChange={e => setQ(e.target.value)} />
        <button type="button" className="kbd key" onClick={closeDialog}>esc</button>
      </div>
      <div id="command-results" className="command-results">
        {results.length ? results.map(r => <button key={r.key} type="button" onClick={() => go(r.to)}>{r.icon}{r.label}<small>{r.kind} ↗</small></button>)
          : <div className="empty-state" style={{ padding: 30 }}><p>No results. Try a page, a campaign or a customer’s name.</p></div>}
      </div>
      <div className="panel-footer"><span>↑ ↓ navigate</span><span>Enter to open · Esc to close</span></div>
    </div>
  );
}

// ── Help & shortcuts ──
export function HelpBody() {
  return <>
    <p>Tether credits every lead and sale to the ads behind it. Every number in this workspace comes from your tracked visits, your connected ad accounts and your sales webhooks.</p>
    <DetailRow label="Search workspace"><span className="kbd">⌘ K / Ctrl K</span></DetailRow>
    <DetailRow label="Close a dialog"><span className="kbd">Esc</span></DetailRow>
    <DetailRow label="Explore campaign details">Click a campaign row</DetailRow>
    <DetailRow label="Change channel credit">Attribution model selector</DetailRow>
    <div className="notice help-steps">
      <span className="help-step"><span className="type-square" aria-hidden="true">1</span>Compare First touch against Last touch.</span>
      <span className="help-step"><span className="type-square" aria-hidden="true">2</span>Open a campaign and explore its journeys.</span>
      <span className="help-step"><span className="type-square" aria-hidden="true">3</span>Save your perspective as a report.</span>
    </div>
  </>;
}
export function HelpFooter() {
  const go = useGo();
  return <button type="button" className="btn primary" onClick={() => go('/attribution?model=first')}><Icon name="target" />Explore attribution</button>;
}

// ── Notifications: the newest tracked activity ──
export function NotificationsBody({ activity }) {
  const items = activity.data || [];
  if (activity.isLoading) return <p>Loading activity…</p>;
  if (activity.isError) return <div className="inline-error" role="alert">Could not load recent activity.</div>;
  if (!items.length) return <p>No tracked activity yet. Install the script to start seeing visits here.</p>;
  return <>{items.map(l => (
    <div className="notification-item" key={l.id || `${l.contact_id}-${l.timestamp}`}><Icon name={l.contact_name || l.contact_email ? 'users' : 'globe'} />
      <div><h3>{l.page_title || 'Page visit'}</h3><p>{l.contact_name || l.contact_email || 'Anonymous visitor'}{l.utm_source ? ` · via ${l.utm_source}` : ''}{l.url ? ` · ${l.url.replace(/^https?:\/\//, '').slice(0, 60)}` : ''}</p><small>{timeAgo(l.timestamp)}</small></div>
    </div>))}</>;
}
export function NotificationsFooter() {
  const go = useGo();
  return <button type="button" className="btn" onClick={() => go('/logs')}><Icon name="activity" />View all activity</button>;
}

// ── Tracking health ──
export function healthFacts({ stats, sales, regs, connectedCount }) {
  const list = sales || [];
  const matched = list.filter(s => s.contact_id).length;
  return [['Events received', num(stats?.total_visits)], ['Events today', num(stats?.today_visits)], ['Contacts tracked', num(stats?.total_contacts)],
    ['Orders received', num(list.length)], ['Matched orders', list.length ? `${num(matched)} (${pct(matched / list.length, 1)})` : '—'], ['Unmatched orders', num(list.length - matched)],
    ...(regs ? [['Registrations received', num(regs.length)]] : []), ['Sources connected', String(connectedCount)]];
}
export function HealthBody({ healthy, facts }) {
  return <>
    <div className={`row ${healthy ? 'positive' : 'muted'}`} style={{ marginBottom: 20 }}><Icon name="shield" />{healthy ? 'All systems operational' : 'No events received today'}</div>
    <p>Live diagnostics for this workspace. Each stage dedupes events and connects purchases to the people and ads behind them.</p>
    {facts.map(([a, b]) => <DetailRow key={a} label={a} strong>{b}</DetailRow>)}
    <Notice>Orders counted from the newest 500 sales received. Matched orders have a tracked person behind them.</Notice>
  </>;
}
export function HealthFooter() {
  const go = useGo();
  return <button type="button" className="btn primary" onClick={() => go('/connections')}><Icon name="plug" />View connections</button>;
}

// ── Workspace ──
export function WorkspaceBody({ me, adminMode }) {
  return <>
    <div className="row"><span className="workspace-logo">{(me?.name || 'T').trim().slice(0, 1).toLowerCase()}.</span>
      <div><h3>{me?.name || 'Workspace'}</h3><p className="muted" style={{ marginTop: 7, fontSize: 12 }}>{me?.email || ''}{adminMode ? ' · Admin session' : ''}</p></div>
      <Badge tone="good" style={{ marginLeft: 'auto' }}>Current</Badge></div>
    <Notice>{me?.tracking_domain && me?.domain_verified ? `Tracking on ${me.tracking_domain}.` : me?.system_tracking_host ? `Tracking on ${me.system_tracking_host}.` : 'Tracking runs on the default Tether host.'} Rename the workspace or change its domain in Workspace settings.</Notice>
  </>;
}
export function WorkspaceFooter({ onSwitchWorkspace }) {
  const go = useGo();
  const { closeDialog } = useDialogs();
  return <>
    {onSwitchWorkspace && <button type="button" className="btn" onClick={() => { closeDialog(); onSwitchWorkspace(); }}><Icon name="compare" />Switch workspace</button>}
    <button type="button" className="btn primary" onClick={() => go('/settings')}><Icon name="settings" />Workspace settings</button>
  </>;
}

// ── Profile ──
export function ProfileBody({ me, adminMode }) {
  return <>
    <div className="journey-profile"><Avatar name={me?.name} size={45} />
      <div><h3>{me?.name || 'Workspace'}</h3><p className="muted" style={{ fontSize: 12, marginTop: 7 }}>{adminMode ? 'Operator · Admin session' : 'Workspace owner'}{me?.email ? ` · ${me.email}` : ''}</p></div></div>
    <Divider />
    <DetailRow label="Workspace">{me?.name || '—'}</DetailRow>
    <DetailRow label="Access">{adminMode ? 'Operator' : 'Owner'}</DetailRow>
    <DetailRow label="Site key"><span className="type-id">{me?.site_key || '—'}</span></DetailRow>
  </>;
}
export function ProfileFooter({ onLogout }) {
  const go = useGo();
  const { closeDialog } = useDialogs();
  return <>
    <button type="button" className="btn" data-testid="sidebar-logout" onClick={() => { closeDialog(); onLogout?.(); }}><Icon name="external" />Log out</button>
    <button type="button" className="btn primary" onClick={() => go('/settings')}><Icon name="settings" />Manage workspace</button>
  </>;
}

export { CloseButton };
